import React, { useEffect, useState, useCallback } from "react";
import { collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import selectCompanyByTicker from "../utils/selectCompanyByTicker";

export default function Leaderboards({ setSelectedCompany, setSelectedPlayer }) {
  const [mode, setMode] = useState("trader");
  const [region, setRegion] = useState("false");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatCompactCurrency = (value) => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (absValue >= 1e12) return sign + "$" + (absValue / 1e12).toFixed(2) + "T";
    if (absValue >= 1e9)  return sign + "$" + (absValue / 1e9).toFixed(2) + "B";
    if (absValue >= 1e6)  return sign + "$" + (absValue / 1e6).toFixed(2) + "M";
    if (absValue >= 1e3)  return sign + "$" + (absValue / 1e3).toFixed(2) + "K";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  };

  const regionMap = {
    false: "GLOBAL",
    AF: "AFRICA",
    AS: "ASIA",
    AU: "AUSTRALIA",
    EU: "EUROPE",
    NA: "N. AMERICA",
    SA: "S. AMERICA",
  };

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const colName = mode === "trader" ? "Leaderboards_Trader" : "Leaderboards_Company";
      let q;
      if (mode === "trader") {
        q = query(
          collection(db, colName),
          ...(region !== "false" ? [where("traderRegion", "==", region)] : []),
          orderBy("traderPortfolio", "desc"),
          limit(100)
        );
      } else {
        q = query(
          collection(db, colName),
          ...(region !== "false" ? [where("companyRegion", "==", region)] : []),
          orderBy("companyValue", "desc"),
          limit(100)
        );
      }
      const snapshot = await getDocs(q);
      setData(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
    }
    setLoading(false);
  }, [mode, region]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return (
    <div style={{ 
      color: "#eee", 
      padding: "10px", 
      fontFamily: "'JetBrains Mono', monospace", 
      fontSize: "0.7rem" 
    }}>
      {/* Industrial Controls */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "15px",
        borderBottom: "1px solid rgba(80, 227, 194, 0.2)",
        paddingBottom: "10px"
      }}>
        <div style={{ display: "flex", gap: "5px" }}>
          {["trader", "company"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                background: mode === m ? "rgba(80, 227, 194, 0.15)" : "transparent",
                border: `1px solid ${mode === m ? "#50e3c2" : "rgba(255,255,255,0.1)"}`,
                color: mode === m ? "#50e3c2" : "#888",
                padding: "4px 12px",
                fontSize: "0.6rem",
                cursor: "pointer",
                textTransform: "uppercase",
                letterSpacing: "1px"
              }}
            >
              {m === "company" ? "COMPANIES" : "TRADERS"}
            </button>
          ))}
        </div>

        <select 
          className="my-select" 
          value={region} 
          onChange={(e) => setRegion(e.target.value)}
          style={{
            background: "#111",
            color: "#50e3c2",
            border: "1px solid rgba(80, 227, 194, 0.3)",
            fontSize: "0.6rem",
            padding: "2px 5px"
          }}
        >
          {Object.entries(regionMap).map(([key, name]) => (
            <option key={key} value={key}>{name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="blink" style={{ color: "#50e3c2" }}>&gt; LOADING...</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "#888", fontSize: "0.6rem", textAlign: "left" }}>
              <th style={{ padding: "8px" }}>RANK</th>
              <th style={{ padding: "8px" }}>{mode === "trader" ? "USERNAME" : "NAME"}</th>
              {mode === "company" && <th style={{ padding: "8px" }}>TICKER</th>}
              <th style={{ padding: "8px", textAlign: "right" }}>MARKET CAP</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              const isTrader = mode === "trader";
              return (
                <tr
                  key={item.id}
                  onClick={() => isTrader 
                    ? setSelectedPlayer(item.traderDisplayName)
                    : selectCompanyByTicker(item.companyTicker, setSelectedCompany)
                  }
                  style={{ 
                    cursor: "pointer", 
                    borderBottom: "1px solid rgba(255,255,255,0.03)",
                    background: index < 3 ? "rgba(80, 227, 194, 0.03)" : "transparent"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(80, 227, 194, 0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = index < 3 ? "rgba(80, 227, 194, 0.03)" : "transparent"}
                >
                  <td style={{ 
                    padding: "8px", 
                    color: index === 0 ? "#ffd700" : index === 1 ? "#c0c0c0" : index === 2 ? "#cd7f32" : "#555",
                    fontWeight: "bold"
                  }}>
                    {String(index + 1).padStart(3, '0')}
                  </td>
                  <td style={{ padding: "8px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ color: "#eee" }}>
                        {isTrader ? item.traderDisplayName : item.companyName}
                      </span>
                      {!isTrader && (
                        <span style={{ fontSize: "0.55rem", color: "#666" }}>
                          CEO: {item.companyCEO}
                        </span>
                      )}
                    </div>
                  </td>
                  {!isTrader && <td style={{ padding: "8px", color: "#50e3c2" }}>{item.companyTicker}</td>}
                  <td style={{ 
                    padding: "8px", 
                    textAlign: "right", 
                    color: "#50e3c2",
                    fontVariantNumeric: "tabular-nums"
                  }}>
                    {formatCompactCurrency(isTrader ? item.traderPortfolio : item.companyValue)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}